import React, { useLayoutEffect, useRef, useState } from 'react';
import Dashboard from '@mui/icons-material/Dashboard';
import Language from '@mui/icons-material/Language';
import Assignment from '@mui/icons-material/Assignment';
import Inventory2 from '@mui/icons-material/Inventory2';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import MonitorHeart from '@mui/icons-material/MonitorHeart';
import BarChart from '@mui/icons-material/BarChart';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useSearchParams } from 'react-router-dom';
import { useProjectWorkflow, WORKFLOW_STEPS } from '../../contexts/ProjectWorkflowContext';
import { useCart } from '../../contexts/CartContext';
import { WorkflowStepId } from '../../types/projectWorkflow';
import { preloadRoute } from '../common/lazyRoute';

const STEP_PRELOAD: Record<WorkflowStepId, string> = {
  overview: '/project-workflow/overview',
  sites: '/sites',
  plan: '/planning',
  order: '/project-workflow/place-order',
  cart: '/project-workflow/cart',
  status: '/guest-blog-placements',
  reporting: '/project-workflow/reporting',
};

function isValidDocUrl(value?: string | null): boolean {
  const v = (value || '').trim();
  if (!v) return false;
  try {
    const parsed = new URL(v);
    return /^https?:$/i.test(parsed.protocol);
  } catch {
    return false;
  }
}

const STEP_ICONS: Record<WorkflowStepId, React.ReactNode> = {
  overview: <Dashboard sx={{ fontSize: 14 }} />,
  sites: <Language sx={{ fontSize: 14 }} />,
  plan: <Assignment sx={{ fontSize: 14 }} />,
  order: <Inventory2 sx={{ fontSize: 14 }} />,
  cart: <ShoppingCart sx={{ fontSize: 14 }} />,
  status: <MonitorHeart sx={{ fontSize: 14 }} />,
  reporting: <BarChart sx={{ fontSize: 14 }} />,
};

/**
 * Current-order completion only (left → right):
 * - Explore / Place Order: based on the active cart (sites selected / docs ready)
 * - Cart / Status / Reporting: only session flags — NOT "project has any past placement"
 * Past completed orders must not paint the whole bar green forever.
 */
const ProjectWorkflowTabs: React.FC = () => {
  const { selectedProject, activeStep, doneSteps, goToStep } = useProjectWorkflow();
  const { cart, clearCart, refreshCart } = useCart(selectedProject?.id);
  const [searchParams] = useSearchParams();
  const wrapRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [fillWidth, setFillWidth] = useState(0);

  // Navigating to Explore Sites should start a FRESH order when a saved order is
  // currently bound (planId in the URL) — clear that order's sites out of the
  // shared cart first. An unbound, in-progress selection (no planId) is kept.
  const handleStepClick = (step: WorkflowStepId) => {
    if (step === 'order' && orderLocked) return;
    if (step === 'cart' && cartLocked) return;
    // Restart Explore: clear a bound saved order, and reset greens after a finished checkout.
    if (step === 'sites') {
      const restart =
        Boolean(searchParams.get('planId')) || doneSteps.cart || doneSteps.status;
      if (restart) {
        void (async () => {
          if (cart && cart.cartItems.length > 0) {
            await clearCart();
            await refreshCart();
          }
          goToStep('sites', { fresh: true });
        })();
        return;
      }
    }
    goToStep(step);
  };

  // All roles share the same Cart & Checkout step. CLIENT/AGENCY pay via Stripe;
  // ADMIN/SUPER_ADMIN confirm the order without payment (direct cart checkout).
  const steps = WORKFLOW_STEPS;

  const cartItems = cart?.cartItems || [];
  const hasCartItems = cartItems.length > 0;
  const allDocsReady =
    hasCartItems && cartItems.every((it) => isValidDocUrl(it.contentUrl || it.contentDocUrl));
  // Place Order needs a site in the cart. Checkout needs a doc link on every site.
  const orderLocked = !hasCartItems;
  const cartLocked = !allDocsReady;

  const effectiveDone = React.useMemo<Record<WorkflowStepId, boolean>>(() => {
    const items = cartItems;
    const hasCartItems = items.length > 0;
    const allDocsReady =
      hasCartItems && items.every((it) => isValidDocUrl(it.contentUrl || it.contentDocUrl));

    // In-progress order only — do not treat historical placements/saved plans as done.
    const hasSiteSelection = hasCartItems || doneSteps.sites || doneSteps.plan;
    const orderReady = allDocsReady || doneSteps.order;

    const raw: Record<WorkflowStepId, boolean> = {
      overview: doneSteps.overview || hasSiteSelection,
      sites: hasSiteSelection,
      plan: hasSiteSelection,
      order: orderReady,
      cart: doneSteps.cart,
      status: doneSteps.status,
      reporting: doneSteps.reporting,
    };

    // Cascade only within the current cart pipeline (not from Status/Reporting history).
    if (raw.order || raw.cart) {
      raw.overview = true;
      raw.sites = true;
      raw.plan = true;
    }
    if (raw.cart) {
      raw.order = true;
    }

    return raw;
  }, [cartItems, doneSteps]);

  // Fill bar to the RIGHT EDGE of the current step (not mid-tab via equal %).
  useLayoutEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current;
      const tabs = tabsRef.current;
      if (!wrap || !tabs) return;
      const buttons = tabs.querySelectorAll<HTMLElement>('.wf-stab');
      if (!buttons.length) return;

      let targetIdx = steps.findIndex((s) => s.id === activeStep);
      if (targetIdx < 0) targetIdx = 0;
      // Include any further completed step so fill never shrinks behind progress.
      steps.forEach((s, i) => {
        if (effectiveDone[s.id]) targetIdx = Math.max(targetIdx, i);
      });

      const btn = buttons[Math.min(targetIdx, buttons.length - 1)];
      if (!btn) return;
      const wrapRect = wrap.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      setFillWidth(Math.max(0, Math.round(btnRect.right - wrapRect.left)));
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeStep, effectiveDone, steps]);

  if (!selectedProject) return null;

  return (
    <div className="wf-step-wrap" ref={wrapRef}>
      <div className="wf-step-tabs" ref={tabsRef}>
        {steps.map((step, i) => {
          const done = effectiveDone[step.id];
          const active = activeStep === step.id;
          const locked = (step.id === 'order' && orderLocked) || (step.id === 'cart' && cartLocked);
          // Overview has no number; remaining steps are 1-based in the badge.
          const num = i === 0 ? null : i;
          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                className={`wf-stab${active ? ' on' : ''}${done && !active ? ' done' : ''}`}
                disabled={locked}
                title={
                  step.id === 'order' && orderLocked
                    ? 'Add at least one site before Place Order'
                    : step.id === 'cart' && cartLocked
                      ? 'Add a document link for each site before Cart & Checkout'
                      : undefined
                }
                onMouseEnter={() => preloadRoute(STEP_PRELOAD[step.id])}
                onFocus={() => preloadRoute(STEP_PRELOAD[step.id])}
                onClick={() => handleStepClick(step.id)}
              >
                {/* Overview: icon + label only — no number/check badge */}
                {step.id !== 'overview' && (
                  <span className={`wf-sbdg${done && !active ? ' done' : active ? ' actv' : ' pend'}`}>
                    {done && !active ? '✓' : num}
                  </span>
                )}
                {STEP_ICONS[step.id]}
                <span>{step.label}</span>
              </button>
              {i < steps.length - 1 && (
                <ChevronRight className="wf-sarr" sx={{ fontSize: 12 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="wf-spbar">
        <div className="wf-spfill" style={{ width: fillWidth }} />
      </div>
    </div>
  );
};

export default ProjectWorkflowTabs;
